import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import EmptyState from "./EmptyState";
import { demoStatus, loadDemoData } from "../lib/sampleData";
import { demoDataAllowed } from "../lib/demoEnvironment";

// Run the demo-data load once, exposing live progress. Guards against a concurrent
// second run; loadDemoData itself is also gated (empty workspace + not already seeded).
export function useDemoLoad(onLoaded) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const run = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError(""); setProgress("Starting…");
    try {
      await loadDemoData({ onProgress: setProgress });
      setProgress("Done.");
      onLoaded && onLoaded();
    } catch (e) { setError(e.message || "Could not load demo data."); }
    finally { setBusy(false); }
  }, [busy, onLoaded]);
  return { run, busy, progress, error };
}

/** A "Load demo data" button with an inline progress line. */
export function LoadDemoDataButton({ onLoaded, label = "✨ Load demo data", confirm = true }) {
  const { run, busy, progress, error } = useDemoLoad(onLoaded);
  const click = () => {
    if (busy) return;
    if (confirm && !window.confirm("Load a full set of realistic sample data into this workspace?")) return;
    run();
  };
  return (
    <div>
      <button style={S.btn()} onClick={click} disabled={busy}>{busy ? "Loading…" : label}</button>
      {busy && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>⏳ {progress}</div>}
      {error && <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>⚠ {error}</div>}
    </div>
  );
}

/**
 * Self-gating empty-state CTA for a list screen: shows a friendly "Get started" panel with
 * the Load button ONLY when the whole workspace is empty and not already seeded; otherwise
 * renders `fallback` (the screen's own "No X yet" empty). `onLoaded` refreshes the screen.
 */
export function GetStartedCTA({ onLoaded, fallback = null, message }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let ok = true;
    demoStatus().then((s) => ok && setStatus(s)).catch(() => ok && setStatus({ workspaceEmpty: false }));
    return () => { ok = false; };
  }, []);
  if (!demoDataAllowed() || !status || !(status.workspaceEmpty && !status.seeded)) return fallback;
  return (
    <EmptyState
      title="Get started"
      message={message || "Your workspace is empty. Load a realistic sample dataset to see Atlas in action across every screen — or add your first record."}
      action={<LoadDemoDataButton onLoaded={onLoaded} />}
    />
  );
}

/** A subtle, dismissible first-run banner (Dashboard). Self-gates to an empty workspace. */
export function DemoDataBanner({ onLoaded }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const { run, busy, progress, error } = useDemoLoad(onLoaded);
  useEffect(() => { demoStatus().then(setStatus).catch(() => setStatus({ workspaceEmpty: false })); }, []);
  if (!demoDataAllowed() || dismissed || !status || !(status.workspaceEmpty && !status.seeded)) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
      <span style={{ fontSize: 20 }}>👋</span>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>New here? Load sample data to see Atlas in action</div>
        <div style={{ color: C.textMuted, fontSize: 12.5 }}>Populates products, customers, a full sales cycle, invoices and expenses across every screen.</div>
        {busy && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>⏳ {progress}</div>}
        {error && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>⚠ {error}</div>}
      </div>
      <button style={S.btn()} onClick={() => { if (!busy) run(); }} disabled={busy}>{busy ? "Loading…" : "✨ Load sample data"}</button>
      <button style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 18 }} aria-label="Dismiss" onClick={() => setDismissed(true)}>✕</button>
    </div>
  );
}

/**
 * OCT-H2 — the app-wide "this IS demo data" label. The mirror image of
 * DemoDataBanner above: that one offers to load a sample dataset into an empty
 * workspace, this one states that one is already loaded.
 *
 * NOT dismissible, deliberately. Its whole purpose is to be in every screenshot
 * and on every screen, and a banner with a ✕ is a banner a presenter closes
 * before the demo starts.
 *
 * Renders null while the lookup is in flight AND if it fails. A DEMO DATA label
 * wrongly shown over a real customer's live books is worse than no label at all,
 * so the error path stays silent rather than guessing.
 */
export function DemoModeBanner() {
  const [seeded, setSeeded] = useState(null);
  useEffect(() => {
    let ok = true;
    demoStatus().then((s) => ok && setSeeded(!!s?.seeded)).catch(() => ok && setSeeded(false));
    return () => { ok = false; };
  }, []);
  if (seeded !== true) return null;
  // Violet: distinct from the amber trial banner and the red read-only banner,
  // so three different app-wide messages never read as the same one.
  return (
    <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: "#241b38", border: `1px solid ${C.purple}`, borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13.5, color: C.text, lineHeight: 1.5 }}>
      <span aria-hidden="true" style={{ fontSize: 16 }}>🧪</span>
      <div style={{ flex: 1, minWidth: 220 }}>
        <strong>DEMO DATA</strong> — This workspace is populated with a sample dataset.
        These customers, invoices and figures are fictional.
      </div>
    </div>
  );
}
